import { FormHelperText as ChakraFormHelperText, forwardRef, Flex } from "@chakra-ui/react";
import { Box } from "@/components/essentials/box";
import { type TypographyProps } from "@/components/essentials/typography";
import { useMemo, useContext } from "react";
import { injectTypographyVariant } from "@/components/essentials/typography/variant";
import { FormControlContext } from "@/components/essentials/form-control/context";
import { Icon } from "@/components/essentials/icon";
import {
  chakraFormHelperTextTestId,
  checkCircleTestId,
  exclamationCircleTestId,
  formHelperTextBoxTestId,
  formHelperTextTestId,
} from "./const";

export interface FormHelperTextProps {
  readonly typography?: TypographyProps["variant"];
  readonly isInvalid?: boolean;
  readonly testIdSuffix?: string;
}

export const FormHelperText = forwardRef<FormHelperTextProps, "div">(
  ({ typography = "text4", isInvalid, testIdSuffix = "", ...rest }, ref) => {
    const typographyProps = useMemo(() => injectTypographyVariant(typography), [typography]);

    const { isDirty, isInvalid: isInvalidContext } = useContext(FormControlContext);

    const invalid = isInvalid !== undefined ? isInvalid : isInvalidContext;
    return (
      <Flex alignItems="center" gridGap={0.5} data-testid={`${formHelperTextTestId}${testIdSuffix}`}>
        {isDirty === true ? (
          invalid === true ? (
            <Icon
              mt="0.5rem"
              as="exclamationCircle"
              color="error.main"
              data-testid={`${exclamationCircleTestId}${testIdSuffix}`}
            />
          ) : (
            <Icon
              mt="0.5rem"
              as="checkCircle"
              color="primary.dark"
              data-testid={`${checkCircleTestId}${testIdSuffix}`}
            />
          )
        ) : (
          <Box mt="0.5rem" w="1rem" data-testid={`${formHelperTextBoxTestId}${testIdSuffix}`} />
        )}
        <ChakraFormHelperText
          ref={ref}
          color={isDirty && invalid ? "error.main" : "gray.dark"}
          data-testid={`${chakraFormHelperTextTestId}${testIdSuffix}`}
          {...typographyProps}
          {...rest}
        />
      </Flex>
    );
  },
);
